/**
 * ✨ SpotFooter.tsx: React component for the Spot tile footer in the FX Cash application.
 *
 * This file contains the SpotFooter component, which manages the RFS (Request For Stream) logic for FX Spot trades.
 * It handles the display and interaction logic for requesting and cancelling RFS, showing progress, and displaying trader information.
 */
import { isNotDefined } from '@sgme/fp';
import deepEqual from 'fast-deep-equal';

import { selectCurrencyPairPrecision } from '@/api/config/selectors.ts';
import { CashTileFooter } from '@/App/tiles/CashTile/shared/CashTileFooter.tsx';
import type { RequestCashRfsParams } from '@/store/cash/api/requestCashRfs.ts';
import { selectCashPriceStatus } from '@/store/cash/selectors/prices.ts';
import { selectCashTile } from '@/store/cash/selectors/tile.ts';
import type { FxCashSpot } from '@/store/cash/state.ts';
import { useAppSelector } from '@/store/hooks.ts';
import { getSignalRConnectionId } from '@/store/streaming/slice';
import { selectTileClientId } from '@/store/tiles/selectors.ts';

export type SpotFooterProps = {
  tileId: string;
};

/**
 * SpotFooter component for the Spot tile footer, handling RFS logic and UI.
 * Uses the shared CashTileFooter component with modern design.
 *
 * @example
 * <SpotFooter tileId="tile-123" />
 */
export const SpotFooter = (props: SpotFooterProps) => {
  const { tileId } = props;

  const { isVisible, isRfsPending, rfsRequestParams, rfsId, isPriceable, remainingTime, totalTime, traderName } =
    useAppSelector((state) => {
      const tile = selectCashTile(state, tileId);

      if (isNotDefined(tile)) {
        return {
          isVisible: false,
          isRfsPending: false,
          isPriceable: false,
        };
      }

      const isPriceable = tile.tradeCapture.status === 'CHECKED' && tile.tradeCapture.isReadyToPrice;

      const signalRConnectionId = getSignalRConnectionId(state);
      const currencyPairPrecision = selectCurrencyPairPrecision(state, tile.properties.currencyPair);

      if (tile.type !== 'FxSpot') {
        return {
          isVisible: false,
          isRfsPending: false,
          isPriceable,
        };
      }

      const clientId = selectTileClientId(state, tileId);
      const rfsRequestParams = getRfsRequestParams(tileId, tile, signalRConnectionId, currencyPairPrecision, clientId);
      const quote = tile.properties.rfs?.status === 'STREAMED' ? tile.properties.rfs.quote : undefined;

      const priceStatus = selectCashPriceStatus(state, tileId);

      return {
        isVisible:
          priceStatus?.status === 'rfs-not-ready' ||
          priceStatus?.status === 'rfs-required' ||
          priceStatus?.status === 'rfs-pending' ||
          priceStatus?.status === 'rfs-price' ||
          priceStatus?.status === 'esp-not-ready' ||
          priceStatus?.status === 'esp-tiering-required' ||
          priceStatus?.status === 'esp-pending',
        rfsRequestParams,
        rfsId: tile.properties.rfs?.rfsId,
        isRfsPending: tile.properties.rfs?.status === 'PENDING',
        isPriceable,
        remainingTime: quote?.rfsRemainingTime,
        totalTime: quote?.rfsWindow,
        traderName: quote?.traderId,
      };
    }, deepEqual);

  return (
    <CashTileFooter
      tileId={tileId}
      isVisible={isVisible}
      isPriceable={isPriceable}
      rfsRequestParams={rfsRequestParams}
      rfsId={rfsId}
      isRfsPending={isRfsPending}
      remainingTime={remainingTime}
      totalTime={totalTime}
      traderName={traderName}
    />
  );
};

/**
 * ⚙️ getRfsRequestParams: Builds the parameters for requesting an RFS for a Spot tile.
 *
 * @param tileId - The tile identifier
 * @param tile - The FxCashSpot tile object
 * @param signalRConnectionId - The SignalR connection ID
 * @param currencyPairPrecision - The precision for the currency pair
 * @param clientId - The client ID
 * @example
 * const params = getRfsRequestParams(tileId, tile, signalRConnectionId, currencyPairPrecision, clientId);
 * 🚩 @returns The parameters for the RFS request, or undefined if not priceable or required data is missing.
 */
const getRfsRequestParams = (
  tileId: string,
  tile: FxCashSpot,
  signalRConnectionId: string | undefined,
  currencyPairPrecision: number,
  clientId: number | undefined,
): RequestCashRfsParams | undefined => {
  // 💡 Check if the tile is priceable
  const isPriceable = tile.tradeCapture.status === 'CHECKED' && tile.tradeCapture.isReadyToPrice;

  // 🚩 Early return if not priceable or required data is missing
  if (
    !isPriceable ||
    isNotDefined(tile.properties.notional) ||
    isNotDefined(tile.properties.notionalCurrencyIndex) ||
    isNotDefined(tile.properties.currency1) ||
    isNotDefined(tile.properties.currency2) ||
    isNotDefined(signalRConnectionId) ||
    isNotDefined(clientId)
  ) {
    return undefined;
  }

  // 💡 Build and return the RFS request parameters
  return {
    tileId,
    productName: 'FxSpot',
    signalRConnectionId,
    clientId,
    currency1: tile.properties.currency1,
    currency2: tile.properties.currency2,
    isCurrencyPairInverted: false,
    notional: tile.properties.notional,
    notionalCurrencyIndex: tile.properties.notionalCurrencyIndex,
    maturityDate: `${tile.properties.maturityDate}T00:00:00.000Z`,
    currencyPairPrecision,
  };
};
